import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Document, Page, pdfjs } from 'react-pdf';
import PropTypes from 'prop-types';
import { map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import LoadingModule from 'common/components/LoadingModule';
import { getInvoiceDetailsApi } from 'api/invoiceGroup';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const InvoiceDetailsViewer = ({ invoiceId, defaultOpen, onAutoOpened }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);

  // Tell the parent the auto-open has been spent, so it stops passing defaultOpen and a dialog
  // the user dismissed doesn't reopen when this row remounts (sorting, paging, void refresh).
  useEffect(() => {
    if (defaultOpen) onAutoOpened();
  }, [defaultOpen, onAutoOpened]);
  const [numPages, setNumPages] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [isError, setIsError] = useState(false);

  const openDialog = () => setOpen(true);
  const closeDialog = () => setOpen(false);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => setNumPages(nextNumPages);

  /* istanbul ignore next */
  const mobilePdfWidth = useMediaQuery(theme.breakpoints.down('sm')) && 300;
  /* istanbul ignore next */
  const tabletPdfWidth = useMediaQuery(theme.breakpoints.down('md')) && 600;
  /* istanbul ignore next */
  const desktopPdfWidth = useMediaQuery(theme.breakpoints.up('md')) && 900;

  const pdfWidth = mobilePdfWidth || tabletPdfWidth || desktopPdfWidth;

  useEffect(() => {
    if (!open || invoiceUrl) {
      return;
    }

    getInvoiceDetailsApi({ invoiceId })
      .then((details) => setInvoiceUrl(details.invoiceUrl))
      .catch(() => {
        setIsError(true);
        enqueueSnackbar('Failed to load invoice PDF', { variant: 'error' });
      });
  }, [invoiceId, invoiceUrl, open]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- renders as a real <button>, not an <a> */}
      <Link component='button' type='button' variant='body2' onClick={openDialog}>
        {invoiceId}
      </Link>

      <Dialog
        open={open}
        maxWidth='lg'
        fullWidth
        fullScreen={!!mobilePdfWidth}
        onClose={closeDialog}
        aria-labelledby='InvoiceGroupInvoices-InvoiceDetailsViewer-header'
      >
        <DialogTitle id='InvoiceGroupInvoices-InvoiceDetailsViewer-header'>
          Invoice {invoiceId}
        </DialogTitle>

        <DialogContent
          sx={{
            p: 0,
            minHeight: invoiceUrl ? undefined : '60vh',
            [theme.breakpoints.up('sm')]: { maxHeight: '70vh' },
          }}
        >
          {isError && <Box sx={{ p: 3 }}>Failed to load invoice</Box>}
          {!invoiceUrl && !isError && <LoadingModule height='40vh' />}
          {invoiceUrl && (
            <Document
              file={invoiceUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              error={<Box sx={{ p: 3 }}>Failed to load invoice</Box>}
              loading={<LoadingModule height='40vh' />}
            >
              {map(new Array(numPages), (_, index) => (
                <Page pageNumber={index + 1} width={pdfWidth} key={index} />
              ))}
            </Document>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            disabled={!invoiceUrl}
            component={Link}
            href={invoiceUrl}
            target='_blank'
            variant='outlined'
            color='secondary'
            fullWidth
            sx={{ mr: 2, maxWidth: 150 }}
          >
            Download
          </Button>

          <Button onClick={closeDialog} variant='contained' fullWidth sx={{ maxWidth: 150 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

InvoiceDetailsViewer.propTypes = {
  invoiceId: PropTypes.number.isRequired,
  // Opens the PDF dialog as soon as the row mounts, so an invoice the user just created or
  // edited shows itself without their having to find and click it in the list.
  defaultOpen: PropTypes.bool,
  onAutoOpened: PropTypes.func,
};

InvoiceDetailsViewer.defaultProps = {
  defaultOpen: false,
  onAutoOpened: () => {},
};

export default InvoiceDetailsViewer;
